import React, { useState, useRef, useEffect } from 'react'
import { Box, Typography, Link as MuiLink } from '@mui/material'
import { styled } from '@mui/material/styles'
import useFileUpload from 'react-use-file-upload'
import { useUploadCsvUserListMutation } from '../../../../../api/integrations'
import userService from 'src/services/user.service'
import LoadingIndicator from 'src/common/components/LoadingIndicator'
import customToastWithAlert from 'src/common/components/CustomToastWithAlert'
import { UploadIcon } from '../../../../../common/icons/UploadIcon'
import { SimpleDownloadIcon } from '../../../../../common/icons/simpleDownloadIcon'
import { useOnboardingContext } from '../../../context/OnboardingContext'

const DropZone = styled(Box)(({ theme, dragOver }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  border: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.grey[400]}`,
  borderRadius: '12px',
  backgroundColor: dragOver ? theme.palette.action.hover : theme.palette.background.default,
  cursor: 'pointer',
  textAlign: 'center',
  minHeight: 280,
  transition: 'border-color 0.3s, background-color 0.3s',
  '& .upload-link span': {
    color: '#0246F8',
    textDecoration: 'none',
    fontWeight: 'bold'
  },
  '& .upload-filesize-msg': {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginTop: theme.spacing(1)
  }
}))

export default function ImportCsvTab() {
  const [dragOver, setDragOver] = useState(false)
  const { files, handleDragDropEvent, setFiles: setFileUploadFiles, clearAllFiles } = useFileUpload()
  const inputRef = useRef()
  const isUploadingRef = useRef(false)

  const [uploadFileTrigger, { isLoading, reset: resetUpload }] = useUploadCsvUserListMutation()

  const { handleCompleteStep, OnboardingStepType, allCards: allSteps } = useOnboardingContext()

  useEffect(() => {
    const uploadAndProcessFile = async () => {
      if (files && files.length > 0 && !isUploadingRef.current) {
        isUploadingRef.current = true

        resetUpload()

        try {
          const response = await uploadFileTrigger(files[0]).unwrap()

          if (response.status === 200 || response.status === 206) {
            customToastWithAlert({
              message: response.data?.description || 'All users imported successfully.',
              type: 'success'
            })

            const importStep = allSteps?.find(step => step.apiType === OnboardingStepType.IMPORT_EMPLOYEES)

            if (importStep && importStep.status !== 'COMPLETED' && handleCompleteStep) {
              handleCompleteStep(OnboardingStepType.IMPORT_EMPLOYEES)
            }
          } else {
            customToastWithAlert({
              message: response.data?.description || 'Import resulted in an unexpected status.',
              type: 'info'
            })
          }
        } catch (error) {
          console.log(error)
          const errorDescription =
            error?.data?.data?.description ||
            error?.data?.message ||
            error?.error ||
            'Failed to import users. Please try again.'
          customToastWithAlert({ message: errorDescription, type: 'error' })
        } finally {
          isUploadingRef.current = false
          clearAllFiles()
        }
      }
    }
    uploadAndProcessFile()
  }, [files, clearAllFiles, handleCompleteStep, uploadFileTrigger, resetUpload, OnboardingStepType, allSteps])

  const handleDownloadExample = async (type = 'CSV') => {
    try {
      const response = await userService.getImportExample(type)
      const contentType = response.headers['content-type']
      const fileExtension = contentType.includes('csv') ? 'csv' : 'xlsx'
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `users_import_example.${fileExtension}`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (e) {
      console.error('Failed to download example file:', e)
      customToastWithAlert({ message: 'Could not download example file.', type: 'error' })
    }
  }

  return (
    <Box>
      {' '}
      <>
        <DropZone
          dragOver={dragOver}
          onDragEnter={e => {
            handleDragDropEvent(e)
            setDragOver(true)
          }}
          onDragLeave={e => {
            handleDragDropEvent(e)
            setDragOver(false)
          }}
          onDragOver={e => {
            handleDragDropEvent(e)
            setDragOver(true)
          }}
          onDrop={e => {
            handleDragDropEvent(e)
            setFileUploadFiles(e)
            setDragOver(false)
          }}
          onClick={() => inputRef.current && inputRef.current.click()}
        >
          <input
            ref={inputRef}
            type='file'
            accept='.csv, .xls, .xlsx'
            style={{ display: 'none' }}
            onChange={e => {
              setFileUploadFiles(e)
              if (inputRef.current) inputRef.current.value = null
            }}
          />
          {isLoading ? (
            <LoadingIndicator />
          ) : (
            <>
              <UploadIcon />
              <Typography className='upload-link' sx={{ mt: 2 }}>
                <span>Click to upload</span> or drag and drop file here
              </Typography>
              <Typography sx={descriptionSx}>CSV or XLSX (5MB max)</Typography>
            </>
          )}
        </DropZone>
        <Box sx={linkWrapperSx}>
          <MuiLink sx={linkSx} onClick={() => handleDownloadExample('CSV')}>
            <SimpleDownloadIcon />
            <span>Download CSV sample</span>
          </MuiLink>
        </Box>
      </>
    </Box>
  )
}

const descriptionSx = {
  fontFamily: 'Plus Jakarta Sans',
  fontWeight: 400,
  fontSize: '14px',
  color: '#76767D'
}

const linkWrapperSx = {
  display: 'flex',
  alignItems: 'center',
  columnGap: '8px',
  justifyContent: 'center',
  mt: 2,
  width: '100%'
}
const linkSx = {
  display: 'flex',
  alignItems: 'center',
  columnGap: '8px',
  justifyContent: 'center',
  fontFamily: 'Plus Jakarta Sans',
  fontWeight: 400,
  fontSize: '16px',
  color: '#0246F8',
  textDecoration: 'none',
  cursor: 'pointer'
}
